import React, { useMemo, useState } from 'react';
import { Box, Divider, ListItemIcon, ListItemText, MenuItem, Menu as MuiMenu } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export type MenuItemOption =
  | 'divider'
  | {
      key?: string;
      label: React.ReactNode;
      onClick?: () => void;
      icon?: React.ReactNode;
      disabled?: boolean;
      href?: string;
      target?: React.HTMLAttributeAnchorTarget;
      color?: 'default' | 'error' | 'success' | 'warning';
    };

export interface MenuProps {
  trigger: React.ReactNode;
  items: MenuItemOption[];
  menuSx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
  keepMounted?: boolean;
}

export default function Menu({ trigger, items, menuSx, itemSx, keepMounted = true }: MenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const renderedItems = useMemo(() => {
    return items.map((item, index) => {
      if (item === 'divider') {
        return <Divider key={`divider-${index}`} />;
      }

      const { key, label, icon, disabled, href, target, onClick, color = 'default' } = item;

      const commonProps = {
        key: key ?? `menu-item-${index}`,
        disabled,
        onClick: () => {
          handleClose();
          onClick?.();
        },
        sx: [
          {
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
            '&:not(:last-of-type)': { mb: 0.25 },
            ...(color !== 'default' && {
              color: (theme: Theme) => (color === 'error' ? theme.palette.error.main : color === 'success' ? theme.palette.success.main : theme.palette.warning.main),
            }),
          },
          itemSx,
        ] as SxProps<Theme>,
      };

      if (href) {
        return (
          <MenuItem component="a" href={href} target={target} {...commonProps}>
            {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        );
      }

      return (
        <MenuItem {...commonProps}>
          {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
          <ListItemText primaryTypographyProps={{ variant: 'body2', fontSize: '0.875rem', fontWeight: 500 }}>{label}</ListItemText>
        </MenuItem>
      );
    });
  }, [items, itemSx]);

  return (
    <>
      <Box component="span" onClick={handleOpen} aria-controls={open ? 'app-menu' : undefined} aria-haspopup="true" aria-expanded={open ? 'true' : undefined} sx={{ display: 'inline-flex', cursor: 'pointer' }}>
        {trigger}
      </Box>

      <MuiMenu
        id="app-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        keepMounted={keepMounted}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: [
              {
                mt: 1,
                minWidth: 200,
                px: 0.5,
                py: 0.5,
                overflow: 'visible',
                borderRadius: 2,
                border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme: Theme) => `0 8px 24px ${theme.palette.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.5)'}`,
                '& .MuiMenuItem-root': {
                  transition: 'background-color 120ms ease, color 120ms ease',
                  '&:hover': {
                    backgroundColor: (theme: Theme) => (theme.palette.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)'),
                  },
                },
                '& .MuiListItemIcon-root': {
                  minWidth: 32,
                },
              },
              menuSx,
            ] as SxProps<Theme>,
          },
        }}
      >
        {renderedItems}
      </MuiMenu>
    </>
  );
}
